import React, {useState} from 'react';
import {Button, Form, Input, message, Modal, Select} from 'antd';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import LanguageSwitcher from 'components/LanguageSwitcher';
import {countryCodeOptions} from 'constants/countryCodes';
import {ResourceCode} from 'constants/resourceCodes';
import {useFetchResourceCodes} from 'hooks/useFetchResourceCodes';
import {useFetchWatermark} from 'hooks/useFetchWatermark';
import {login, removeUserInfo, saveUserInfo} from 'utils/auth';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {safeMobileRedirect} from './mobilePaths';

interface LoginValues {
  countryCode: number;
  phone: string;
  password: string;
}

/**
 * 客诉手机版登录（后台账号：国家码 + 手机号 + 密码，交互参考 PDA 登录页）。
 * 端类型由 utils/auth.login 按 UA 上报为 WEB_MOBILE，与 PC 浏览器会话互不踢；
 * 准入看资源码 CS_COMPLAINT_TASK_WORK（与 PC 作业端同码，10-14 确认），不按账号角色拦。
 */
const MobileLoginPage: React.FC = () => {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const {fetchResourceCodes} = useFetchResourceCodes();
  const {fetchWatermark} = useFetchWatermark();

  const attempt = async (values: LoginValues, confirmKickout: boolean) => {
    setLoading(true);
    try {
      const result = await login(values.countryCode || 1, values.phone.trim(), values.password, confirmKickout);
      const userInfo = result?.userInfo;
      if (!userInfo) {
        if (result?.requireKickConfirm) {
          Modal.confirm({
            title: t('complaintMobile.login.kickTitle'),
            content: t('complaintMobile.login.kickContent'),
            okText: t('complaintMobile.login.kickOk'),
            cancelText: t('common.cancel'),
            onOk: () => attempt(values, true),
          });
          return;
        }
        message.error(result?.messageKey === 'errors.business.account.locked'
          ? (result.displayMessage || t('errors.business.account.locked'))
          : t('errors.login.failed'));
        return;
      }
      saveUserInfo(userInfo);
      const language = userInfo.account.language?.toLowerCase().split('-')[0];
      if (language && language !== i18n.language) {
        await i18n.changeLanguage(language);
      }
      let codes: string[] = [];
      try {
        codes = await fetchResourceCodes();
      } catch (e) {
        message.error(translateGraphQLError(e, 'errors.auth.fetchResourceCodesFailed'));
      }
      if (!codes.includes(ResourceCode.cs.CS_COMPLAINT_TASK_WORK)) {
        removeUserInfo();
        message.error(t('complaintMobile.login.noPermission'));
        return;
      }
      // 水印规范：首屏业务数据出来前水印要在
      void fetchWatermark().catch((e) => console.warn('Fetch watermark failed', e));
      navigate(safeMobileRedirect(params.get('redirect')), {replace: true});
    } catch (e) {
      message.error(translateGraphQLError(e, 'errors.login.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col px-6 pt-4 pb-8">
      <div className="flex justify-end"><LanguageSwitcher/></div>
      <div className="mt-12 mb-10 text-center">
        <div className="text-2xl font-bold text-gray-800">{t('complaintMobile.title')}</div>
        <div className="text-gray-500 mt-2">{t('complaintMobile.login.subtitle')}</div>
      </div>
      <Form<LoginValues>
        layout="vertical"
        size="large"
        initialValues={{countryCode: 1}}
        onFinish={(v) => attempt(v, false)}
        requiredMark={false}
      >
        <Form.Item label={t('login.phone.label')} required>
          <div className="flex gap-2">
            <Form.Item name="countryCode" noStyle>
              <Select className="!w-24" options={countryCodeOptions}/>
            </Form.Item>
            <Form.Item name="phone" noStyle rules={[{required: true, message: t('login.phone.required') ?? ''}]}>
              <Input inputMode="tel" autoComplete="username" placeholder={t('login.phone.placeholder') ?? ''}/>
            </Form.Item>
          </div>
        </Form.Item>
        <Form.Item name="password" label={t('login.password.label')}
                   rules={[{required: true, message: t('login.password.required') ?? ''}]}>
          <Input.Password autoComplete="current-password" placeholder={t('login.password.placeholder') ?? ''}/>
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading} className="!h-12 mt-4">
          {t('login.submit')}
        </Button>
      </Form>
    </div>
  );
};

export default MobileLoginPage;
